import { useCallback, useEffect, useReducer, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { BaseLogo, getLogoOrgId } from "../core/models/entities/BaseLogo";
import { ErrorStatus } from "../core/models/entities/ErrorStatus";
import { useApiClient } from "../data/clients/ApiClient";
import {
  cancelInFlightDetailFetch,
  isLogoDetailFromList,
  logoDetailKey,
  patchLogoRowsFromDetail,
} from "../data/repositories/logoDetailCache";
import {
  showErrorToast,
  showInfoToast,
} from "../presentation/components/Toast/Toast";

// Admin "Refresh prices now" (flag REACT_APP_LOGO_PRICE_REFRESH_NOW_ENABLED;
// Nichlas 12/9: "kan vi altid fra admin-menuen få en pris hurtigere igen?").
// One click = ONE POST organizations/{orgId}/logos/{logoId}/product-refresh.
// The API evicts the NAV FindTemplate2 / JetSport GetTemplate caches for the
// logo, stamps it product-pending, re-queues the existing price worker and
// puts the serial on the price batch queue; the answer (202) carries the
// stamp it wrote and the cooldown it will enforce (Redis claim -> 429 with
// retryAfterSeconds on a second click). NOTHING here runs on render or on an
// effect: only `refresh` - a click - costs a call. 0 GET calls: the detail
// entry (["logos", actAs, id]) and the list rows are patched to "Prices on
// their way" from the answer, the popup's existing pending poll
// (useProductPendingPoll, <= 3 GetLogo) and the list poll clear the badge
// when the worker has landed, exactly as after a resize.
//
// Module state, shared by every button on the page: the logos with a click
// in the air, the cooldown instant per logo and the one global pause (the
// API's daily cap) - so the lock holds when the popup is closed and opened
// again, and a second button for the same logo (list row + popup, later the
// editor) reads the same state.

export type PriceRefreshNowState = "idle" | "loading" | "cooldown";

/** Why the button is in "cooldown": the per-logo window, or the API's global daily cap. */
export type PriceRefreshNowLockKind = "cooldown" | "dailyCap";

/** The API's default per-logo cooldown (LogoPriceRefreshNowCooldownSeconds), used when an answer carries none. */
export const PRICE_REFRESH_NOW_DEFAULT_COOLDOWN_S = 60;

/** errorCode of the API's 409 "the logo is already pending and its stamp is younger than the cooldown". */
export const PRICE_REFRESH_ALREADY_PENDING_CODE = "alreadyPending";

/** errorCode of the API's 429 "the global daily cap (LogoPriceRefreshNowDailyCap) is spent for this UTC day". */
export const PRICE_REFRESH_DAILY_CAP_CODE = "dailyCapReached";

// 409 codes whose `message` is shown as the toast: deterministic refusals
// the API explains better than a generic text (only image logos have a
// template; no vendor organisation / NAV number).
const API_MESSAGE_409_CODES = new Set(["notImageLogo", "noVendor"]);

const cooldownUntilMs = new Map<number, number>();
let dailyCapUntilMs = 0;
const inFlight = new Set<number>();
let version = 0;
const listeners = new Set<() => void>();

function notify(): void {
  version += 1;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): number {
  return version;
}

function setCooldown(logoId: number, untilMs: number): void {
  cooldownUntilMs.set(logoId, untilMs);
  notify();
}

/** The instant the cooldown of `logoId` ends (ms since epoch), undefined when it has none. */
export function getPriceRefreshCooldownUntilMs(logoId: number): number | undefined {
  return cooldownUntilMs.get(logoId);
}

export function resetLogoPriceRefreshNowForTests(): void {
  cooldownUntilMs.clear();
  dailyCapUntilMs = 0;
  inFlight.clear();
  version = 0;
  listeners.clear();
}

// The lock in effect for a logo at `nowMs`: in flight, the daily cap (every
// logo), the logo's own cooldown - or none. The later of the two instants
// wins when both are set, so the tooltip never promises a click the API
// would refuse.
function lockOf(
  logoId: number,
  nowMs: number
): { state: PriceRefreshNowState; untilMs: number; kind: PriceRefreshNowLockKind } {
  if (inFlight.has(logoId)) return { state: "loading", untilMs: 0, kind: "cooldown" };
  const own = cooldownUntilMs.get(logoId) ?? 0;
  const cap = dailyCapUntilMs;
  const untilMs = Math.max(own, cap);
  if (untilMs > nowMs) {
    return {
      state: "cooldown",
      untilMs,
      kind: cap >= own && cap > nowMs ? "dailyCap" : "cooldown",
    };
  }
  return { state: "idle", untilMs: 0, kind: "cooldown" };
}

// Whole seconds from an API field (cooldownSeconds / retryAfterSeconds);
// anything but a positive finite number = the default.
function seconds(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? Math.ceil(value)
    : fallback;
}

// The ErrorStatus of a DataError (AuthorizedFetch); undefined for transport
// errors (fetch rejected, aborted) and anything else that is not ours.
function errorStatusOf(error: unknown): ErrorStatus | undefined {
  const status = (error as { errorStatus?: ErrorStatus } | null | undefined)
    ?.errorStatus;
  return status && typeof status.code === "number" ? status : undefined;
}

/**
 * The state of the "refresh prices now" button of one logo and the click
 * handler behind it.
 *
 * `refresh()` (ignored unless the state is "idle"):
 *  - 202: the cooldown starts (the answer's cooldownSeconds, default 60 s);
 *    the cached DETAIL entry - only a real GetLogo, never the popup-fast
 *    list stub (isLogoDetailFromList; the button is disabled until the
 *    detail has landed, this guard is the belt to those braces) - gets
 *    productPending=true + the stamp the API wrote (ONLY those two fields
 *    change), and the same is copied into the list rows of this act-as
 *    scope (patchLogoRowsFromDetail, the #784 helper). A GetLogo of that
 *    key still in flight at the click (the hi-res heal's 5/15/45 s re-read
 *    of a fresh image logo) is cancelled first (cancelInFlightDetailFetch,
 *    the mutation flows' rule): landing AFTER the 202 it would overwrite
 *    the patch with its pre-click productPending=false - badge gone, poll
 *    never armed, old price until the popup is reopened. 0 calls (react-
 *    query drops the cancelled answer). Writing the detail makes it the
 *    newest entry, so the lists are patched, the popup wrapper sees
 *    realDetail.productPending and arms useProductPendingPoll with a fresh
 *    3/8/15 s budget (the key left its set when pending last cleared; a
 *    re-driven STALE logo keeps its spent budget - the list poll takes the
 *    row over once the popup closes, see the PR's known limits). One short
 *    info toast ("customer prices update within a few minutes": the
 *    serial's price row lands with the price-batch drain).
 *  - 429 rateLimited: cooldown = the answer's retryAfterSeconds (the API's
 *    Redis claim is the source of truth - another tab, a colleague), info
 *    toast "try again in n s". No cache write.
 *  - 429 dailyCapReached: the API's GLOBAL daily cap is spent - every
 *    button on the page is paused for retryAfterSeconds (default 60 s; the
 *    API answers 429 again if clicked after that), info toast "daily limit
 *    reached". No cache write.
 *  - 409 alreadyPending: a mutation or the worker is on it - info toast,
 *    cooldown = retryAfterSeconds. No cache write (the row already says
 *    pending, or will on the next read).
 *  - 409 notImageLogo / noVendor: deterministic - the API's own message as
 *    the error toast + the default cooldown so the button is not hammered
 *    for nothing.
 *  - 503 queueUnavailable (the stamp stands, the sweep re-queues it within
 *    10 min): "queue unavailable - try again" error toast, the button is
 *    idle again.
 *  - any other 4xx (409 disabled, 404 route or logo, 403): generic error
 *    toast + the default cooldown.
 *  - other 5xx and transport errors: generic error toast, idle again - a
 *    retry may work, and the API's own claim answers 429 if not.
 * The state is recomputed on every store change and once when the lock
 * ends (ONE timer to that instant, no per-second interval): the tooltip's
 * "n s" is the remaining time at the last render, not a live countdown.
 * Call budget: 1 HTTP call per accepted click, 0 otherwise, 0 on render.
 */
export function useLogoPriceRefreshNow(logo: BaseLogo): {
  state: PriceRefreshNowState;
  lockKind: PriceRefreshNowLockKind;
  retryInSeconds: number;
  refresh: () => void;
} {
  const queryClient = useQueryClient();
  const { refreshLogoPricesNow } = useApiClient();
  const { t } = useTranslation();
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const logoId = logo.id;
  const orgId = getLogoOrgId(logo);
  const now = Date.now();
  const { state, untilMs, kind } = lockOf(logoId, now);
  const retryInSeconds =
    state === "cooldown" ? Math.max(1, Math.ceil((untilMs - now) / 1000)) : 0;

  // One timer to the end of the lock, so the button opens again without a
  // store change; cleared when the lock moves or the view unmounts.
  useEffect(() => {
    if (state !== "cooldown") return;
    const timer = setTimeout(rerender, Math.max(untilMs - Date.now(), 0) + 1);
    return () => clearTimeout(timer);
  }, [state, untilMs]);

  const refresh = useCallback(() => {
    if (!(logoId > 0)) return;
    if (lockOf(logoId, Date.now()).state !== "idle") return;
    inFlight.add(logoId);
    notify();

    refreshLogoPricesNow(orgId, logoId)
      .then((response) => {
        setCooldown(
          logoId,
          Date.now() +
            seconds(response?.cooldownSeconds, PRICE_REFRESH_NOW_DEFAULT_COOLDOWN_S) *
              1000
        );
        const key = logoDetailKey(logoId);
        const prev = queryClient.getQueryData<BaseLogo>(key);
        if (prev && !isLogoDetailFromList(prev)) {
          cancelInFlightDetailFetch(queryClient, logoId);
          const next: BaseLogo = {
            ...prev,
            productPending: true,
            productPendingSinceUtc:
              typeof response?.pendingSinceUtc === "string"
                ? response.pendingSinceUtc
                : new Date().toISOString(),
          };
          queryClient.setQueryData<BaseLogo>(key, next);
          patchLogoRowsFromDetail(queryClient, next);
        }
        showInfoToast(t("compLogoDesign.priceRefreshQueued"), 5000);
      })
      .catch((error: unknown) => {
        const status = errorStatusOf(error);
        if (status?.code === 429) {
          const wait = seconds(
            status.retryAfterSeconds,
            PRICE_REFRESH_NOW_DEFAULT_COOLDOWN_S
          );
          if (status.errorCode === PRICE_REFRESH_DAILY_CAP_CODE) {
            dailyCapUntilMs = Math.max(dailyCapUntilMs, Date.now() + wait * 1000);
            notify();
            showInfoToast(t("compLogoDesign.priceRefreshDailyCap"), 6000);
            return;
          }
          setCooldown(logoId, Date.now() + wait * 1000);
          showInfoToast(
            t("compLogoDesign.priceRefreshTryAgain", { n: wait }),
            4000
          );
          return;
        }
        if (
          status?.code === 409 &&
          status.errorCode === PRICE_REFRESH_ALREADY_PENDING_CODE
        ) {
          const wait = seconds(
            status.retryAfterSeconds,
            PRICE_REFRESH_NOW_DEFAULT_COOLDOWN_S
          );
          setCooldown(logoId, Date.now() + wait * 1000);
          showInfoToast(t("compLogoDesign.priceRefreshAlreadyPending"), 4000);
          return;
        }
        if (status?.code === 503) {
          showErrorToast(t("compLogoDesign.priceRefreshQueueUnavailable"));
          return;
        }
        const apiMessage =
          status?.code === 409 &&
          typeof status.errorCode === "string" &&
          API_MESSAGE_409_CODES.has(status.errorCode) &&
          typeof status.message === "string" &&
          status.message.trim() !== ""
            ? status.message
            : undefined;
        showErrorToast(apiMessage ?? t("compLogoDesign.priceRefreshFailed"));
        if (status && status.code >= 400 && status.code < 500) {
          setCooldown(
            logoId,
            Date.now() + PRICE_REFRESH_NOW_DEFAULT_COOLDOWN_S * 1000
          );
        }
      })
      .finally(() => {
        inFlight.delete(logoId);
        notify();
      });
  }, [logoId, orgId, queryClient, refreshLogoPricesNow, t]);

  return { state, lockKind: kind, retryInSeconds, refresh };
}
